import type {
  PsdArchiveResult,
  PsdDropResult,
  PsdPlacement,
  PsdTree,
  PsdTreeObject,
  SetDetail,
  SetSummary,
} from '@epc/contracts';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import clsx from 'clsx';
import { ChevronDown, ChevronRight, Folder, FolderArchive, FolderUp } from 'lucide-react';
import { type ReactNode, useMemo, useRef, useState } from 'react';
import { api } from '../../lib/api';
import { Button } from '../../ui/button';
import { Dialog } from '../../ui/dialog';
import { Badge, EmptyState, ErrorBox, Spinner, toast } from '../../ui/feedback';
import { Field, Select, Textarea } from '../../ui/form';
import { ScrollX } from '../../ui/scroll';

/**
 * ПСД проекта папками (поручение владельца 2.11.2026): пятно (объект проекта, вложенно) → раздел (марка) →
 * альбом. «Без объекта» — отдельной папкой. Счётчики на каждом уровне: альбомов, в производстве, на проверке,
 * в карантине, открытых вопросов и замечаний.
 */

export const NO_OBJECT = 'none';

export const psdTreeQuery = (projectId: string) => ({
  queryKey: ['psd-tree', projectId],
  queryFn: () => api<PsdTree>(`/psd/projects/${projectId}/tree`),
});

interface Counts {
  sets: number;
  production: number;
  checking: number;
  quarantine: number;
  open: number;
}
const count = (sets: SetSummary[]): Counts => ({
  sets: sets.length,
  production: sets.filter((s) => s.activeRevision).length,
  checking: sets.filter((s) => s.lastStatus === 'review' || s.lastStatus === 'approved').length,
  quarantine: sets.filter((s) => s.state === 'quarantine').length,
  open: sets.reduce((n, s) => n + s.openQuestions + s.openRemarks, 0),
});

function CountBadges({ c }: { c: Counts }) {
  return (
    <span className="flex flex-wrap items-center gap-1">
      <Badge>альбомов {c.sets}</Badge>
      {c.production ? <Badge tone="ink">в производстве {c.production}</Badge> : null}
      {c.checking ? <Badge tone="info">на проверке {c.checking}</Badge> : null}
      {c.quarantine ? <Badge tone="danger">карантин {c.quarantine}</Badge> : null}
      {c.open ? <Badge tone="warning">вопросы и замечания {c.open}</Badge> : null}
    </span>
  );
}

/** Путь объекта от корня: для крошек и подписи */
export function objectPath(objects: PsdTreeObject[], id: string | undefined): PsdTreeObject[] {
  const out: PsdTreeObject[] = [];
  let cur = id ? objects.find((o) => o.id === id) : undefined;
  const seen = new Set<string>();
  while (cur && !seen.has(cur.id)) {
    seen.add(cur.id);
    out.unshift(cur);
    cur = cur.parentId ? objects.find((o) => o.id === cur!.parentId) : undefined;
  }
  return out;
}

/** Объект и все вложенные в него */
function subtree(objects: PsdTreeObject[], id: string): Set<string> {
  const out = new Set([id]);
  for (let grew = true; grew; ) {
    grew = false;
    for (const o of objects)
      if (o.parentId && out.has(o.parentId) && !out.has(o.id)) {
        out.add(o.id);
        grew = true;
      }
  }
  return out;
}

const objLabel = (o: PsdTreeObject) => (o.code ? `${o.code} · ${o.name}` : o.name);

/** Список объектов для выбора: с отступом по вложенности */
export function objectOptions(objects: PsdTreeObject[]) {
  const out: Array<{ id: string; label: string }> = [];
  const walk = (parent: string | null, depth: number) => {
    for (const o of objects.filter((x) => (x.parentId ?? null) === parent && x.id !== parent)) {
      out.push({ id: o.id, label: `${'  '.repeat(depth)}${objLabel(o)}` });
      if (depth < 20) walk(o.id, depth + 1);
    }
  };
  // Корни — объекты без родителя или с родителем вне списка
  const ids = new Set(objects.map((o) => o.id));
  for (const o of objects.filter((x) => !x.parentId || !ids.has(x.parentId))) {
    out.push({ id: o.id, label: objLabel(o) });
    walk(o.id, 1);
  }
  return out;
}

function FolderRow({
  title,
  depth,
  open,
  onToggle,
  counts,
  extra,
  icon,
}: {
  title: ReactNode;
  depth: number;
  open: boolean;
  onToggle: () => void;
  counts: Counts;
  extra?: ReactNode;
  icon?: ReactNode;
}) {
  return (
    <div
      className="flex flex-col gap-1.5 border-t border-line py-2 pr-4 md:flex-row md:items-center md:gap-3"
      style={{ paddingLeft: `${12 + Math.min(depth, 6) * 16}px` }}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={open}
        className="flex min-h-11 min-w-0 flex-1 items-center gap-2 text-left text-[14px] font-medium text-ink md:min-h-9"
      >
        {open ? <ChevronDown className="size-4 shrink-0" /> : <ChevronRight className="size-4 shrink-0" />}
        {icon ?? <Folder className="size-4 shrink-0 text-ink-2" />}
        <span className="min-w-0 [overflow-wrap:anywhere]">{title}</span>
      </button>
      <span className="flex flex-wrap items-center gap-2 pl-6 md:pl-0">
        <CountBadges c={counts} />
        {extra}
      </span>
    </div>
  );
}

function SetRow({ s, depth }: { s: SetSummary; depth: number }) {
  return (
    <Link
      to="/psd/sets/$setId"
      params={{ setId: s.id }}
      className="flex min-h-11 flex-col gap-1 border-t border-line py-2 pr-4 hover:bg-surface-2 md:flex-row md:items-center md:gap-3"
      style={{ paddingLeft: `${12 + Math.min(depth, 6) * 16 + 24}px` }}
    >
      <span className="min-w-0 flex-1">
        <span className="block text-[14px] font-medium text-ink [overflow-wrap:anywhere]">
          {s.markNo ? `Альбом ${s.markNo} · ` : ''}
          {s.code}
        </span>
        <span className="block text-[13px] text-ink-2 [overflow-wrap:anywhere]">
          {s.name} · листов {s.sheetCount}
        </span>
      </span>
      <span className="flex flex-wrap gap-1">
        {s.activeRevision ? (
          <Badge tone="ink">{s.activeRevision} в производстве</Badge>
        ) : (
          <Badge>не выдан</Badge>
        )}
        {s.lastStatus === 'review' || s.lastStatus === 'approved' ? (
          <Badge tone="info">{s.lastRevision} на проверке</Badge>
        ) : s.lastStatus === 'development' || s.lastStatus === 'remarks' ? (
          <Badge>{s.lastRevision} в работе</Badge>
        ) : null}
        {s.state === 'quarantine' ? <Badge tone="danger">карантин</Badge> : null}
        {s.openQuestions ? <Badge tone="warning">вопросов {s.openQuestions}</Badge> : null}
        {s.openRemarks ? <Badge tone="warning">замечаний {s.openRemarks}</Badge> : null}
      </span>
    </Link>
  );
}

/** Разделы (марки) внутри папки объекта: альбомы по номеру */
function Marks({
  sets,
  depth,
  keyPrefix,
  isOpen,
  toggle,
}: {
  sets: SetSummary[];
  depth: number;
  keyPrefix: string;
  isOpen: (k: string) => boolean;
  toggle: (k: string) => void;
}) {
  const byMark = new Map<string, SetSummary[]>();
  for (const s of sets) {
    const k = s.mark ?? 'Без марки';
    byMark.set(k, [...(byMark.get(k) ?? []), s]);
  }
  return (
    <>
      {[...byMark.entries()]
        .sort(([a], [b]) => a.localeCompare(b, 'ru'))
        .map(([mark, list]) => {
          const k = `${keyPrefix}:m:${mark}`;
          const sorted = [...list].sort(
            (a, b) =>
              (a.markNo ?? '').localeCompare(b.markNo ?? '', 'ru', { numeric: true }) ||
              a.code.localeCompare(b.code),
          );
          return (
            <div key={k}>
              <FolderRow
                title={`Раздел ${mark}`}
                depth={depth}
                open={isOpen(k)}
                onToggle={() => toggle(k)}
                counts={count(list)}
              />
              {isOpen(k) ? sorted.map((s) => <SetRow key={s.id} s={s} depth={depth + 1} />) : null}
            </div>
          );
        })}
    </>
  );
}

export function PsdTreeView({
  projectId,
  objectId,
  mark,
  onFilter,
}: {
  projectId: string;
  objectId?: string;
  mark?: string;
  onFilter: (f: { object?: string; mark?: string }) => void;
}) {
  const q = useQuery(psdTreeQuery(projectId));
  const [open, setOpen] = useState<Set<string>>(() => new Set());
  const toggle = (k: string) =>
    setOpen((cur) => {
      const n = new Set(cur);
      if (n.has(k)) n.delete(k);
      else n.add(k);
      return n;
    });
  const marks = useMemo(
    () => [...new Set((q.data?.sets ?? []).map((s) => s.mark).filter((m): m is string => Boolean(m)))].sort(),
    [q.data],
  );
  if (q.isPending) return <Spinner />;
  if (q.isError) return <ErrorBox error={q.error} />;
  const data = q.data;
  const { objects } = data;
  const known = new Set(objects.map((o) => o.id));
  // Фильтр: объект с вложенными и марка
  const inObject =
    objectId === NO_OBJECT ? null : objectId && known.has(objectId) ? subtree(objects, objectId) : undefined;
  const sets = data.sets.filter(
    (s) =>
      (!mark || s.mark === mark) &&
      (objectId === NO_OBJECT
        ? !s.objectId || !known.has(s.objectId)
        : !inObject || (s.objectId ? inObject.has(s.objectId) : false)),
  );
  const setsUnder = (id: string) => {
    const ids = subtree(objects, id);
    return sets.filter((s) => s.objectId && ids.has(s.objectId));
  };
  const loose = sets.filter((s) => !s.objectId || !known.has(s.objectId));
  // Под фильтром найденное раскрыто сразу; нажатие сворачивает папку (и наоборот без фильтра)
  const auto = Boolean(objectId || mark);
  const isOpen = (k: string) => (auto ? !open.has(k) : open.has(k));

  const renderObject = (o: PsdTreeObject, depth: number, seen: Set<string>): ReactNode => {
    // Петля в дереве объектов (A → B → A) не уводит в бесконечную вложенность
    if (seen.has(o.id)) return null;
    const path = new Set(seen).add(o.id);
    const under = setsUnder(o.id);
    if (!under.length && (objectId || mark)) return null;
    const k = `o:${o.id}`;
    const own = sets.filter((s) => s.objectId === o.id);
    const kids = objects.filter((x) => x.parentId === o.id && x.id !== o.id);
    return (
      <div key={k}>
        <FolderRow
          title={objLabel(o)}
          depth={depth}
          open={isOpen(k)}
          onToggle={() => toggle(k)}
          counts={count(under)}
          extra={
            <span className="flex flex-wrap gap-x-3 text-[13px]">
              <button
                type="button"
                className="inline-flex min-h-11 items-center text-ink underline-offset-4 hover:underline md:min-h-0"
                onClick={() => onFilter({ object: o.id, mark })}
              >
                Только этот объект
              </button>
              {data.canEstimates ? (
                <Link
                  to="/estimates"
                  search={{ projectId }}
                  className="inline-flex min-h-11 items-center text-ink underline-offset-4 hover:underline md:min-h-0"
                >
                  Сметы проекта
                </Link>
              ) : null}
              {data.canSchedule ? (
                <Link
                  to="/schedule/$projectId"
                  params={{ projectId }}
                  search={{} as never}
                  className="inline-flex min-h-11 items-center text-ink underline-offset-4 hover:underline md:min-h-0"
                >
                  График проекта
                </Link>
              ) : null}
            </span>
          }
        />
        {isOpen(k) ? (
          <>
            {kids.map((c) => renderObject(c, depth + 1, path))}
            <Marks sets={own} depth={depth + 1} keyPrefix={k} isOpen={isOpen} toggle={toggle} />
          </>
        ) : null}
      </div>
    );
  };
  // Корни — объекты без родителя; объекты замкнутой петли без корня тоже показываются корнями, чтобы их альбомы
  // не пропали
  const roots = objects.filter((o) => !o.parentId || !known.has(o.parentId));
  const reached = new Set<string>();
  for (const r of roots) for (const id of subtree(objects, r.id)) reached.add(id);
  for (const o of objects)
    if (!reached.has(o.id)) {
      roots.push(o);
      for (const id of subtree(objects, o.id)) reached.add(id);
    }
  const noObjKey = `o:${NO_OBJECT}`;
  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-col gap-3 md:flex-row md:items-end">
        <Field label="Объект (пятно)">
          <Select
            className="h-11 md:h-10 md:w-72"
            value={objectId ?? ''}
            onChange={(e) => onFilter({ object: e.target.value || undefined, mark })}
          >
            <option value="">Все объекты</option>
            {objectOptions(objects).map((o) => (
              <option key={o.id} value={o.id}>
                {o.label}
              </option>
            ))}
            <option value={NO_OBJECT}>Без объекта</option>
          </Select>
        </Field>
        <Field label="Раздел (марка)">
          <Select
            className="h-11 md:h-10 md:w-48"
            value={mark ?? ''}
            onChange={(e) => onFilter({ object: objectId, mark: e.target.value || undefined })}
          >
            <option value="">Все разделы</option>
            {marks.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </Select>
        </Field>
        {objectId || mark ? (
          <Button variant="ghost" className="h-11 md:h-10" onClick={() => onFilter({})}>
            Сбросить
          </Button>
        ) : null}
      </div>
      {!data.sets.length ? (
        <EmptyState title="Альбомов пока нет">
          Загрузите папку или архив с выдачей проектировщика: файлы разложатся по объектам, разделам и
          альбомам по шифру, нераспознанное попадёт в карантин.
        </EmptyState>
      ) : !sets.length ? (
        <EmptyState title="Под фильтр ничего не попало">Выберите другой объект или раздел.</EmptyState>
      ) : (
        <div className="overflow-hidden rounded-lg border border-line bg-surface [&>div:first-child>div:first-child]:border-t-0">
          {roots.map((o) => renderObject(o, 0, new Set()))}
          {loose.length ? (
            <div>
              <FolderRow
                title="Без объекта"
                depth={0}
                open={isOpen(noObjKey)}
                onToggle={() => toggle(noObjKey)}
                counts={count(loose)}
              />
              {isOpen(noObjKey) ? (
                <Marks sets={loose} depth={1} keyPrefix={noObjKey} isOpen={isOpen} toggle={toggle} />
              ) : null}
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}

/* ───────────── Перенос альбома в другой объект ───────────── */

export function MoveSetDialog({
  set: s,
  onClose,
  onDone,
}: {
  set: SetDetail;
  onClose: () => void;
  onDone: (d: SetDetail) => void;
}) {
  const tree = useQuery(psdTreeQuery(s.projectId));
  const qc = useQueryClient();
  const [objectId, setObjectId] = useState(s.objectId ?? NO_OBJECT);
  const [comment, setComment] = useState('');
  const m = useMutation({
    mutationFn: () =>
      api<SetDetail>(`/psd/sets/${s.id}/move`, {
        method: 'POST',
        body: { objectId: objectId === NO_OBJECT ? null : objectId, comment: comment.trim() || null },
      }),
    onSuccess: (d) => {
      qc.invalidateQueries({ queryKey: ['psd-tree', s.projectId] });
      qc.invalidateQueries({ queryKey: ['psd-sets'] });
      onDone(d);
      toast('Альбом перенесён');
      onClose();
    },
  });
  return (
    <Dialog
      open
      onOpenChange={(v) => !v && onClose()}
      title="Перенести альбом в другой объект"
      footer={
        <>
          <Button variant="ghost" className="h-11 md:h-10" onClick={onClose}>
            Отмена
          </Button>
          <Button variant="primary" className="h-11 md:h-10" loading={m.isPending} onClick={() => m.mutate()}>
            Перенести
          </Button>
        </>
      }
    >
      <div className="grid gap-4">
        <p className="text-[14px] text-ink-2">
          Шифр не меняется. Перенос попадёт в журнал ревизии и историю изменений.
        </p>
        {tree.isPending ? (
          <Spinner />
        ) : (
          <Field label="Объект (пятно)">
            <Select className="h-11 md:h-10" value={objectId} onChange={(e) => setObjectId(e.target.value)}>
              {objectOptions(tree.data?.objects ?? []).map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
              <option value={NO_OBJECT}>Без объекта</option>
            </Select>
          </Field>
        )}
        <Field label="Причина (необязательно)">
          <Textarea rows={2} value={comment} onChange={(e) => setComment(e.target.value)} />
        </Field>
        <ErrorBox error={tree.error ?? m.error} />
      </div>
    </Dialog>
  );
}

/* ───────────── Загрузка папкой или архивом ───────────── */

const ACTION_LABEL: Record<PsdPlacement['action'], string> = {
  existing: 'в альбом',
  new: 'новый альбом',
  quarantine: 'карантин',
  skip: 'пропущен',
};

type Picked = { file: File; path: string };

export function FolderUpload({ projectId }: { projectId: string }) {
  const qc = useQueryClient();
  const dirRef = useRef<HTMLInputElement>(null);
  const zipRef = useRef<HTMLInputElement>(null);
  const [picked, setPicked] = useState<Picked[] | null>(null);
  const [plan, setPlan] = useState<PsdPlacement[] | null>(null);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [result, setResult] = useState<PsdArchiveResult | null>(null);
  const [error, setError] = useState<unknown>(null);
  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['psd-tree', projectId] });
    qc.invalidateQueries({ queryKey: ['psd-sets'] });
  };

  const onFolder = async (list: FileList | null) => {
    if (!list?.length) return;
    const files = [...list].map((f) => ({
      file: f,
      path: (f as File & { webkitRelativePath?: string }).webkitRelativePath || f.name,
    }));
    setError(null);
    setResult(null);
    if (files.length > 2000) {
      setError(
        new Error(`В папке ${files.length} файлов — за один раз не больше 2000. Загрузите её частями.`),
      );
      return;
    }
    try {
      const p = await api<PsdPlacement[]>(`/psd/projects/${projectId}/upload-plan`, {
        method: 'POST',
        body: { paths: files.map((f) => f.path) },
      });
      setPicked(files);
      setPlan(p);
    } catch (e) {
      setError(e);
    }
  };

  const upload = async () => {
    if (!picked || !plan) return;
    const todo = picked.filter((f) => plan.find((x) => x.path === f.path && x.action !== 'skip'));
    const placed: PsdDropResult[] = [];
    const skipped = plan
      .filter((x) => x.action === 'skip')
      .map((x) => ({ path: x.path, reason: x.problems[0] ?? '' }));
    setProgress({ done: 0, total: todo.length });
    for (const [i, f] of todo.entries()) {
      const form = new FormData();
      form.append('path', f.path);
      form.append('file', f.file, f.file.name);
      try {
        placed.push(await api<PsdDropResult>(`/psd/projects/${projectId}/drop`, { method: 'POST', form }));
      } catch (e) {
        skipped.push({ path: f.path, reason: (e as Error).message });
      }
      setProgress({ done: i + 1, total: todo.length });
    }
    setProgress(null);
    setPlan(null);
    setPicked(null);
    setResult({ placed, skipped });
    refresh();
  };

  const zip = useMutation({
    mutationFn: (f: File) => {
      const form = new FormData();
      form.append('file', f, f.name);
      return api<PsdArchiveResult>(`/psd/projects/${projectId}/archive`, { method: 'POST', form });
    },
    onSuccess: (r) => {
      setResult(r);
      refresh();
    },
  });

  return (
    <>
      <input
        ref={dirRef}
        type="file"
        className="hidden"
        multiple
        // Выбор папки целиком: браузер передаёт путь каждого файла внутри папки
        {...({ webkitdirectory: '', directory: '' } as Record<string, string>)}
        onChange={(e) => {
          void onFolder(e.target.files);
          e.target.value = '';
        }}
      />
      <input
        ref={zipRef}
        type="file"
        accept=".zip"
        className="hidden"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) zip.mutate(f);
          e.target.value = '';
        }}
      />
      <Button
        size="lg"
        icon={<FolderUp className="size-4" />}
        title="На iPhone и iPad папку выбрать нельзя — загрузите архив ZIP"
        onClick={() => dirRef.current?.click()}
      >
        Загрузить папку
      </Button>
      <Button
        size="lg"
        icon={<FolderArchive className="size-4" />}
        loading={zip.isPending}
        onClick={() => zipRef.current?.click()}
      >
        Загрузить архив ZIP
      </Button>
      {error || zip.error ? (
        <div className="w-full">
          <ErrorBox error={error ?? zip.error} />
        </div>
      ) : null}
      {plan ? (
        <Dialog
          open
          onOpenChange={(v) => !v && !progress && setPlan(null)}
          title="Куда лягут файлы"
          footer={
            <>
              <Button
                variant="ghost"
                className="h-11 md:h-10"
                disabled={Boolean(progress)}
                onClick={() => setPlan(null)}
              >
                Отмена
              </Button>
              <Button
                variant="primary"
                className="h-11 md:h-10"
                loading={Boolean(progress)}
                disabled={!plan.some((x) => x.action !== 'skip')}
                onClick={() => void upload()}
              >
                {progress ? `Загружено ${progress.done} из ${progress.total}` : 'Загрузить'}
              </Button>
            </>
          }
        >
          <PlanTable rows={plan} />
        </Dialog>
      ) : null}
      {result ? (
        <Dialog
          open
          onOpenChange={(v) => !v && setResult(null)}
          title="Загрузка завершена"
          footer={
            <Button variant="primary" className="h-11 md:h-10" onClick={() => setResult(null)}>
              Готово
            </Button>
          }
        >
          <div className="grid gap-3 text-[14px]">
            <p>
              Разложено файлов: {result.placed.filter((x) => !x.duplicate).length}
              {result.placed.some((x) => x.duplicate)
                ? `, уже были загружены раньше: ${result.placed.filter((x) => x.duplicate).length}`
                : ''}
              {result.placed.some((x) => x.quarantine)
                ? `, из них в карантин: ${result.placed.filter((x) => x.quarantine).length}`
                : ''}
              . Не загружено: {result.skipped.length}.
            </p>
            {result.skipped.length ? (
              <ul className="grid gap-1 text-[13px] text-ink-2">
                {result.skipped.slice(0, 50).map((x) => (
                  <li key={x.path} className="[overflow-wrap:anywhere]">
                    {x.path} — {x.reason}
                  </li>
                ))}
              </ul>
            ) : null}
          </div>
        </Dialog>
      ) : null}
    </>
  );
}

function PlanTable({ rows }: { rows: PsdPlacement[] }) {
  const n = (a: PsdPlacement['action']) => rows.filter((r) => r.action === a).length;
  return (
    <div className="grid gap-3">
      <p className="text-[14px] text-ink-2">
        В найденные альбомы: {n('existing')}, в новые: {n('new')}, в карантин: {n('quarantine')}, пропущено:{' '}
        {n('skip')}. Карантин — альбом с шифром папки: его шифр поправят, и он встанет на место.
      </p>
      <ScrollX>
        <table className="w-full min-w-[520px] text-[13px]">
          <thead>
            <tr className="text-left text-ink-2">
              <th className="py-1.5 pr-2 font-medium">Файл</th>
              <th className="py-1.5 pr-2 font-medium">Куда</th>
              <th className="py-1.5 font-medium">Объект, раздел</th>
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 500).map((r) => (
              <tr key={r.path} className="border-t border-line align-top">
                <td className="py-1.5 pr-2 [overflow-wrap:anywhere]">{r.path}</td>
                <td className="py-1.5 pr-2">
                  <Badge
                    tone={r.action === 'quarantine' ? 'danger' : r.action === 'skip' ? 'warning' : 'neutral'}
                  >
                    {ACTION_LABEL[r.action]}
                  </Badge>
                  <div
                    className={clsx('mt-0.5 [overflow-wrap:anywhere]', r.action === 'skip' && 'text-ink-2')}
                  >
                    {r.action === 'skip' ? r.problems[0] : r.code}
                  </div>
                </td>
                <td className="py-1.5 text-ink-2">
                  {[r.objectName ?? (r.action === 'skip' ? null : 'без объекта'), r.mark]
                    .filter(Boolean)
                    .join(' · ')}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </ScrollX>
    </div>
  );
}
